import * as React from 'react';

import { Modal } from '@mc/react-core';
import { Form } from 'react-final-form';
import { ElementOf, tuple } from '../common/props';
import { ReactNode, useCallback, useMemo } from 'react';
import * as Classes from '../common/classes';
import { FilterString } from './FilterString';
import { FilterNumber } from './FilterNumber';
import classNames from 'classnames';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { FilterFieldVO } from '@mc/data-filter-react';
import { FilterNumberRange } from './FilterNumberRange';
import { FilterDate } from './FilterDate';
import { FilterDateRange } from './FilterDateRange';
import { FilterDateTimeRange } from './FilterDateTimeRange';
import { VFFilterFieldProps } from '../form/types';
import {
  FilterFieldProps,
  FilterType,
  getFormFieldObjectIdsValue,
  getFormFieldObjectIdValue,
  SavedFiltersType,
} from './types';
import { FilterCheckbox, FilterChexkboxOwnProps } from './FilterCheckbox';
import { FilterMultiSelect } from './FilterMultiSelect';
import { FilterSelectBox } from './FilterSelectBox';
import {
  Autocomplete,
  CancellableApiPromise,
} from '../autocomplete/Autocomplete';
import { FilterAutocomplete } from './FilterAutocomplete';
import { ButtonToolbar } from '../button-toolbar/ButtonToolbar';
import { ManageVisibleFieldsModalForm } from './ManageVisibleFieldsModalForm';
import { Checkbox as CheckboxCore } from '../checkbox/Checkbox';
import { StringInput } from '../string/StringInput';
import { FilterApiMultiSelect } from './FilterApiMultiSelect';
import { ApiMultiSelectSearch } from '../multiselect/ApiMultiSelectSearch';
import { mcDelete, mcErase, mcFilterConfig, mcSave } from '@mc/react-ui-icons';
import { FilterDateTime } from './FilterDateTime';
import { Buffer } from 'buffer';
import { ButtonGroup } from '../button-group/ButtonGroup';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { MenuDivider } from '../menu/MenuDivider';
import { LAST_FILTER_ID, useFilters } from './useFilters';
import { FilterTime } from './FilterTime';
import { FilterTimeRange } from './FilterTimeRange';
import { ClearFilterConfig } from '../rows-data/types';

export type FilterFieldType = ElementOf<typeof FilterFieldTypes>;
const FilterFieldTypes = tuple(
  'text',
  'integer',
  'integerRange',
  'decimal',
  'decimalRange',
  'date',
  'dateRange',
  'time',
  'timeRange',
  'checkbox',
  'currency',
  'currencyRange',
  'currencyDecimal',
  'currencyDecimalRange',
  'dateTime',
  'dateTimeRange',
  'multiSelect',
  'apiMultiSelect',
  'selectBox',
  'autocomplete',
  'custom'
);
export type SerialiseFilterOptions = {
  /**
   * Pokud je true, tak na cílové stránce uměle vyvolá přenačtení dat v tabulce stejně jako "vyhledat"
   */
  refetch?: boolean;
};
export type SerialiseFilterObject = {
  filter?: any;
  /**
   * `options` je objekt, který obsahuje další volitelné nastavení pro serializaci filtru.
   */
  options?: SerialiseFilterOptions;
};
type FilterCompProps = {
  filterDef: FilterDef;
  /**
   * Field může vyvolat spuštění hledání, např. na enter apod.
   */
  onSearch: () => void;
};

export interface FilterListItem {
  id: any | null;
  name?: string | ReactNode;
  nazev?: string | ReactNode;
}

export type ExtendedFilter = {
  /**
   * Pokud je true, tak se u dané položky dá filtrovat vyplněnost/nevyplněnost hodnoty. Tímto se zapíná pro konkrétní položku. Filtrování lze zapnout globálně pro všechny položky pomocí `filterByFilled` na Filters a případně pro jednu položku přes nastavení na false vypnout.
   */
  filterByFilled?: boolean;
  /**
   * Pokud je true, tak se podmínka otáčí, tedy místo "=" se použije "!=".
   */
  filterByNot?: boolean;
};

export type FilterDef = {
  type: FilterFieldType;
  name: string;
  label: string;
  component?: (props: FilterCompProps) => ReactNode;
  size?: number;
  textProps?: React.ComponentProps<typeof StringInput>;
  getInitialValue?: (filterType: FilterType) => FilterFieldVO<any> | any;
  convertQueryValue?: (formValue: any) => any;
  multiSelectProps?: { items: FilterListItem[]; title?: React.ReactNode };
  timeProps?: { referenceDate?: Date; onlyTime?: boolean };
  /**
   * Pokud je true, tak se u dané položky dá filtrovat vyplněnost/nevyplněnost hodnoty. Tímto se zapíná pro konkrétní položku. Filtrování lze zapnout globálně pro všechny položky pomocí `filterByFilled` na Filters a případně pro jednu položku přes nastavení na false vypnout.
   * @deprecated nahrazeno extendedFilter
   */
  filterByFilled?: boolean;
  /**
   * Extended nastavení filtru, např. pro přepínání mezi "je vyplněno" a "není vyplněno", nebo přepínání mezi "=" a "!=".
   * Lze zapnout globálně pro všechny položky na Filters a případně pro jednu položku zase vypnout.
   */
  extendedFilter?: ExtendedFilter;
  numberProps?: {
    /**
     * Pokud je true, tak input nebude oddělovat tisíce.
     */
    noThousandsSeparator?: boolean;
    /**
     * Pokud je true, lze zadávat pouze kladná čísla a 0.
     */
    onlyPositive?: boolean;
    /**
     * Pokud je true, je možné zadat desetinná čísla.
     */
    decimal?: boolean;
    /**
     * Na kolik desetinných míst - max., výchozí hodnota je 2.
     */
    maximumFractionDigits?: number;
  };
  apiMultiSelectProps?: Omit<
    // @ts-ignore TS by jinak brečel, nemá cenu pokoušet se o opravu.
    React.ComponentProps<typeof ApiMultiSelectSearch<unknown>>,
    'onChange' | 'value'
  > & { title?: React.ReactNode };
  selectBoxProps?: {
    items: FilterListItem[];
    allowNullValue?: boolean;
  };
  autocompleteProps?: Omit<
    React.ComponentProps<typeof Autocomplete<unknown>>,
    'onSearchItems'
  > & {
    onSearchItems: (text: string) => CancellableApiPromise<FilterListItem[]>;
    getItemsByIds?: (ids: (number | string)[]) => Promise<any[]>;
  };
  checkboxProps?: React.ComponentProps<typeof CheckboxCore> &
    FilterChexkboxOwnProps;
};

type Props<FORM extends object> = {
  type?: FilterType;
  className?: string;
  initialVisibleFields?: string[];
  filtersDef: FilterDef[];
  formFilter?: FORM;
  setFilter: (formData: FORM, queryData: FORM) => void;
  getClearFilter: (config: ClearFilterConfig) => any;
  visibleFields?: string[];
  setVisibleFields?: (fields: string[]) => void;
  manageVisibleFields?: boolean;
  visibleClearField?: boolean;
  /**
   * Klíč / Identifikace pro ukládání nastavení (např. filtry, profily apod.).
   */
  settingsKey?: string;
  visibleFilterProfiles?: boolean;
  /**
   * Pokud je true, tak se u u položek dá filtrovat vyplněnost/nevyplněnost hodnoty. Tímto se zapíná globálně pro všechny (kromě checkbox). Filtrování lze pro jednu položku přímo na položce vypnout.
   * @deprecated nahrazeno extendedFilter
   */
  filterByFilled?: boolean;
  /**
   * Extended nastavení filtru, např. pro přepínání mezi "je vyplněno" a "není vyplněno", nebo přepínání mezi "=" a "!=".
   * Lze zapnout globálně pro všechny položky na Filters a případně pro jednu položku zase vypnout.
   */
  extendedFilter?: ExtendedFilter;
};

type FilterDefUpd = FilterDef & {
  component: (props: FilterCompProps) => ReactNode | undefined;
};

type FieldMap = { [key: string]: FilterDefUpd };

export const isRangeValue = (type: FilterFieldType): boolean => {
  switch (type) {
    case 'integerRange':
    case 'currencyDecimalRange':
    case 'currencyRange':
    case 'dateRange':
    case 'timeRange':
    case 'dateTimeRange':
      return true;
    default:
      return false;
  }
};

export const filterUrlParamName = 'filters';

export const serializeFilter = (
  filter?: any,
  options?: SerialiseFilterOptions
) => {
  let tempObj = { filter: filter || '', options: options || {} };
  let s = JSON.stringify(tempObj);
  const val = Buffer.from(s).toString('base64');
  return val;
};

export const deserializeFilter = (
  value?: string | undefined | null
): undefined | SerialiseFilterObject => {
  if (value === undefined || value === null) {
    return undefined;
  }
  try {
    if (value === '') {
      return undefined;
    } else {
      return JSON.parse(Buffer.from(value, 'base64').toString());
    }
  } catch (e) {
    console.log('Data filtru se nepodařilo deserializovat', value);
    return {};
  }
};

const filtersGetInitialValue = (
  filter: FilterDef,
  filterType: FilterType
): FilterFieldVO<any> | any => {
  if (filter.getInitialValue) {
    return filter.getInitialValue(filterType);
  }

  return filtersGetEmptyValue(filter, filterType);
};

export const filtersValuesChanged = (
  filterType: FilterType,
  prevFilterDef: FilterDef,
  formPrevData: any,
  newFilterDef: FilterDef,
  formNewData: any
): boolean => {
  let prevValue;
  let newValue;

  switch (filterType) {
    case 'default':
      prevValue = formPrevData[prevFilterDef.name].value;
      newValue = formNewData[newFilterDef.name].value;
      break;
    case 'onlyValues':
      prevValue = formPrevData[prevFilterDef.name];
      newValue = formNewData[newFilterDef.name];
      break;
  }

  if (Array.isArray(newValue)) {
    if (!Array.isArray(prevValue)) {
      return true;
    }

    if (prevValue.length !== newValue.length) {
      return true;
    }

    for (let a = 0; a < newValue.length; a++) {
      if (prevValue[a] != newValue[a]) {
        return true;
      }
    }

    return false;
  } else {
    return newValue != prevValue;
  }
};

export const filtersGetEmptyValue = (
  filter: FilterDef,
  filterType: FilterType
): FilterFieldVO<any> | any => {
  switch (filterType) {
    case 'default':
      if (isRangeValue(filter.type)) {
        return {
          value: [undefined, undefined],
          emptyValue: false,
          notEmptyValue: false,
        };
      } else {
        switch (filter.type) {
          case 'custom':
            return {
              value: undefined,
              emptyValue: false,
              notEmptyValue: false,
            };
          case 'checkbox': {
            return {
              value: filter.checkboxProps?.allowNullValue ? undefined : false,
              emptyValue: false,
              notEmptyValue: false,
            };
          }
          case 'multiSelect': {
            return {
              value: [],
              emptyValue: false,
              notEmptyValue: false,
            };
          }
          case 'apiMultiSelect': {
            return {
              value: [],
              emptyValue: false,
              notEmptyValue: false,
            };
          }
          default:
            return {
              value: undefined,
              emptyValue: false,
              notEmptyValue: false,
            };
        }
      }
    case 'onlyValues':
      if (isRangeValue(filter.type)) {
        return [undefined, undefined];
      } else {
        switch (filter.type) {
          case 'custom':
            return undefined;
          case 'checkbox':
            return filter.checkboxProps?.allowNullValue ? undefined : false;
          case 'multiSelect':
          case 'apiMultiSelect':
            return [];
          default:
            return undefined;
        }
      }
  }
};

export const filtersComputeFormInitialValues = (
  filterType: FilterType,
  formInitData: any,
  filtersDef: FilterDef[]
) => {
  const vals: any = {
    ...formInitData,
  };

  filtersDef.forEach((filter) => {
    filtersFillFieldInitialValue(filterType, vals, filter);
  });
  return vals;
};

const isEmpty = (v: any) => {
  if (v === null || v === undefined || v === '') {
    return true;
  } else {
    return false;
  }
};

export const filtersIsFieldValueFilled = (
  filterType: FilterType = 'default',
  vals: any,
  filter: FilterDef
) => {
  const filterVal = vals[filter.name];
  if (filterVal === undefined) {
    return false;
  }

  switch (filterType) {
    case 'default':
      if (
        filterVal.emptyValue ||
        filterVal.notEmptyValue ||
        filterVal.notValue
      ) {
        return true;
      }

      if (isEmpty(filterVal)) {
        return false;
      }

      if (isRangeValue(filter.type)) {
        return !isEmpty(filterVal.value[0]) || !isEmpty(filterVal.value[1]);
      } else {
        switch (filter.type) {
          case 'custom':
            // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
            return !isEmpty(filterVal.value);
          case 'checkbox': {
            return filter.checkboxProps?.allowNullValue
              ? filterVal.value === true || filterVal.value === false
              : filterVal.value === true;
          }
          case 'multiSelect':
          case 'apiMultiSelect':
            return filterVal.value.length > 0;
          default:
            return !isEmpty(filterVal.value);
        }
      }
    case 'onlyValues':
      if (isRangeValue(filter.type)) {
        return !isEmpty(filterVal[0]) || !isEmpty(filterVal[1]);
      } else {
        switch (filter.type) {
          case 'custom':
            // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
            return !isEmpty(filterVal);
          case 'checkbox':
            return filter.checkboxProps?.allowNullValue
              ? filterVal === true || filterVal === false
              : filterVal === true;
          case 'multiSelect':
          case 'apiMultiSelect':
            return filterVal.length > 0;
          default:
            return !isEmpty(filterVal);
        }
      }
  }
};

export const filtersGetFieldValue = (
  filterType: FilterType = 'default',
  vals: any,
  filter: FilterDef
) => {
  const filterVal = vals[filter.name];

  switch (filterType) {
    case 'default':
      if (!filterVal) {
        return undefined;
      }

      if (filterVal.emptyValue) {
        return undefined;
      }

      return filterVal.value;
    case 'onlyValues':
      return filterVal;
  }
};

export const filtersFillFieldInitialValue = (
  filterType: FilterType,
  vals: any,
  filter: FilterDef
) => {
  switch (filterType) {
    case 'default':
      vals[filter.name] = {
        ...filtersGetInitialValue(filter, filterType),
        ...vals[filter.name],
      };
      break;
    case 'onlyValues':
      switch (filter.type) {
        case 'checkbox':
          vals[filter.name] =
            vals[filter.name] !== undefined
              ? vals[filter.name]
              : filtersGetInitialValue(filter, filterType);
          break;
        default:
          vals[filter.name] =
            vals[filter.name] || filtersGetInitialValue(filter, filterType);
          break;
      }
      break;
  }
};

export const filtersClearFieldInitial = (
  filterType: FilterType,
  vals: any,
  filter: FilterDef
) => {
  vals[filter.name] = filtersGetEmptyValue(filter, filterType);
};

export const filtersComputeQueryFilter = (
  filterType: FilterType,
  formData: any,
  filtersDef: FilterDef[]
): any => {
  const result: any = {};

  filtersDef.forEach((filter) => {
    if (filter.convertQueryValue) {
      result[filter.name] = {
        ...formData[filter.name],
        value: filter.convertQueryValue(formData[filter.name].value),
      };
    } else {
      switch (filter.type) {
        case 'autocomplete':
          result[filter.name] = getFormFieldObjectIdValue(
            filter.name,
            filterType,
            formData
          );
          break;
        case 'apiMultiSelect':
          result[filter.name] = getFormFieldObjectIdsValue(
            filter.name,
            filterType,
            formData
          );
          break;
        default:
          result[filter.name] = formData[filter.name];
          break;
      }
    }

    // Odebrání bool hodnot pro emptyValue a notEmptyValue - pokud jsou false, neposíláme je (takto hlavně z důvodu zpětné kopatibility kdy by to padalo na serveru po přidání notEmptyValue - byla by to breaking change a to nechceme)
    if (filterType === 'default' && result[filter.name]) {
      if (!result[filter.name].emptyValue) {
        delete result[filter.name].emptyValue;
      }
      if (!result[filter.name].notEmptyValue) {
        delete result[filter.name].notEmptyValue;
      }
    }
  });

  return result;
};

export const getFilterComponent = (
  filterDef: Omit<FilterDef, 'label'> & { label?: string },
  type: FilterType,
  extendedFilter?: ExtendedFilter
) => {
  // Default z Filters
  const extendedFilterConf = {
    ...extendedFilter,
  };
  // filterByFilled z položky filtru
  if (typeof filterDef.filterByFilled !== 'undefined') {
    extendedFilterConf.filterByFilled =
      filterDef.filterByFilled ?? extendedFilter?.filterByFilled;
  }
  // extended filtr z položky filtru
  if (filterDef.extendedFilter) {
    if (typeof filterDef.extendedFilter.filterByFilled !== 'undefined') {
      extendedFilterConf.filterByFilled =
        filterDef.extendedFilter.filterByFilled;
    }
    if (typeof filterDef.extendedFilter.filterByNot !== 'undefined') {
      extendedFilterConf.filterByNot = filterDef.extendedFilter.filterByNot;
    }
  }

  console.log(
    '############',
    extendedFilter,
    filterDef.filterByFilled,
    filterDef.extendedFilter,
    extendedFilterConf
  );
  // ---

  const filterFieldProps: FilterFieldRenderProps = {
    name: filterDef.name,
    label: filterDef.label,
    size: filterDef.size,
    extendedFilter: extendedFilterConf,
    filterType: type,
  };

  let component: (props: FilterCompProps) => ReactNode | undefined = () =>
    undefined;

  switch (filterDef.type) {
    case 'text':
      component = ({ onSearch }) => (
        <FilterString
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.textProps}
        />
      );
      break;
    case 'integer':
      component = ({ onSearch }) => (
        <FilterNumber
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={false}
        />
      );
      break;
    case 'autocomplete':
      component = ({ onSearch }) => (
        <FilterAutocomplete
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.autocompleteProps}
          // @ts-ignore
          onSearchItems={
            filterDef.autocompleteProps?.onSearchItems ||
            (async () => {
              return [];
            })
          }
        />
      );
      break;
    case 'selectBox':
      component = ({ onSearch }) => (
        <FilterSelectBox
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.selectBoxProps}
          items={filterDef?.selectBoxProps?.items || []}
        />
      );
      break;
    case 'multiSelect':
      component = ({ onSearch }) => (
        <FilterMultiSelect
          onSearch={onSearch}
          {...filterFieldProps}
          title={filterDef?.multiSelectProps?.title}
          items={filterDef?.multiSelectProps?.items || []}
        />
      );
      break;
    case 'apiMultiSelect':
      component = ({ onSearch }) => (
        <FilterApiMultiSelect
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.apiMultiSelectProps}
          searchItems={
            filterDef?.apiMultiSelectProps?.searchItems || (async () => [])
          }
        />
      );
      break;
    case 'integerRange':
      component = ({ onSearch }) => (
        <FilterNumberRange
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={false}
        />
      );
      break;
    case 'decimal':
      component = ({ onSearch }) => (
        <FilterNumber
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={true}
        />
      );
      break;
    case 'decimalRange':
      component = ({ onSearch }) => (
        <FilterNumber
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={true}
          alignRight
        />
      );
      break;
    case 'date':
      component = ({ onSearch }) => (
        <FilterDate onSearch={onSearch} {...filterFieldProps} />
      );
      break;
    case 'time':
      component = ({ onSearch }) => (
        <FilterTime
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.timeProps}
        />
      );
      break;
    case 'dateRange':
      component = ({ onSearch }) => (
        <FilterDateRange onSearch={onSearch} {...filterFieldProps} />
      );
      break;
    case 'timeRange':
      component = ({ onSearch }) => (
        <FilterTimeRange
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.timeProps}
        />
      );
      break;
    case 'checkbox':
      component = ({ onSearch }) => (
        <FilterCheckbox
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.checkboxProps}
        />
      );
      break;
    case 'currency':
      component = ({ onSearch }) => (
        <FilterNumber
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={false}
          alignRight
        />
      );
      break;
    case 'currencyRange':
      component = ({ onSearch }) => (
        <FilterNumberRange
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={false}
        />
      );
      break;
    case 'currencyDecimal':
      component = ({ onSearch }) => (
        <FilterNumber
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={true}
          alignRight
        />
      );
      break;
    case 'currencyDecimalRange':
      component = ({ onSearch }) => (
        <FilterNumberRange
          onSearch={onSearch}
          {...filterFieldProps}
          {...filterDef?.numberProps}
          decimal={true}
        />
      );
      break;
    case 'custom':
      // @ts-ignore
      component = filterDef.component;
      break;
    case 'dateTime':
      component = ({ onSearch }) => (
        <FilterDateTime onSearch={onSearch} {...filterFieldProps} />
      );
      break;
    case 'dateTimeRange':
      component = ({ onSearch }) => (
        <FilterDateTimeRange onSearch={onSearch} {...filterFieldProps} />
      );
      break;
    default:
      component = () => undefined;
      break;
  }

  return component;
};

export type FilterFieldRenderProps = Omit<
  VFFilterFieldProps<FilterFieldProps>,
  'onSearch'
>;

export const Filters = <FORM extends object>({
  className,
  type = 'default',
  initialVisibleFields,
  filtersDef,
  setVisibleFields,
  formFilter,
  setFilter,
  getClearFilter,
  visibleFields,
  manageVisibleFields,
  visibleClearField = true,
  settingsKey,
  visibleFilterProfiles = true,
  filterByFilled,
  extendedFilter = {},
}: Props<FORM>) => {
  const extendedFilterConf: ExtendedFilter = {
    ...extendedFilter,
  };
  if (typeof filterByFilled !== 'undefined') {
    extendedFilterConf.filterByFilled = filterByFilled;
  }

  const realManageVisibleFields =
    manageVisibleFields === undefined
      ? filtersDef.length > 6
      : manageVisibleFields;
  const formInitialValues = useMemo(() => {
    return filtersComputeFormInitialValues(type, formFilter, filtersDef);
  }, [formFilter, filtersDef, type]);

  const filterHook = useFilters(settingsKey);
  const onSavedFilter = useCallback((filterItem: SavedFiltersType) => {
    const formData = filterItem.filter;
    const queryData = filtersComputeQueryFilter(type, formData, filtersDef);
    //@ts-ignore
    setFilter(formData, queryData);
    //@ts-ignore
    setVisibleFields?.(filterItem?.visibleFields ?? visibleFields);
  }, []);

  return (
    <Form<FORM>
      onSubmit={(formData) => {
        // @ts-ignore
        const queryData = filtersComputeQueryFilter(type, formData, filtersDef);

        // Uložení posledního použitého filtru
        if (visibleFilterProfiles && settingsKey) {
          filterHook.saveLastFilter(formData, visibleFields);
        }

        setFilter(formData, queryData);
      }}
      initialValues={formInitialValues}
    >
      {(props) => {
        const onSearch = useCallback(() => {
          props.handleSubmit();
        }, [props.handleSubmit]);

        const filtersMapUpdated = useMemo<FieldMap>(() => {
          const res: FieldMap = {};

          filtersDef.forEach((filter) => {
            const component = getFilterComponent(
              filter,
              type,
              extendedFilterConf
            );

            res[filter.name] = {
              ...filter,
              component,
            };
          });

          return res;
        }, [filtersDef]);

        const showManageVisibleFields = () => {
          const filledFilterFields: string[] = [];
          filtersDef.forEach((filterDef) => {
            if (filtersIsFieldValueFilled(type, formFilter, filterDef)) {
              filledFilterFields.push(filterDef.name);
            }
          });

          // @ts-ignore
          Modal.show(({ onClose }) => (
            <ManageVisibleFieldsModalForm
              filledFilterFields={filledFilterFields}
              // @ts-ignore
              onClose={onClose}
              filtersDef={filtersDef}
              visibleFields={visibleFields}
              onChange={(newFields) => {
                const prevFields = visibleFields
                  ? visibleFields
                  : filtersDef.map((x) => x.name);
                const deletedFields = prevFields.filter(
                  (f) => newFields.indexOf(f) === -1
                );

                // Pokud nějaký filtr odeberu ze seznamu viditelných, zruším nastavení jeho filtru
                let deletedFieldsValueChanged = false;
                if (deletedFields.length > 0) {
                  props.form.pauseValidation();
                  filtersDef.forEach((filterDef) => {
                    if (deletedFields.indexOf(filterDef.name) >= 0) {
                      // @ts-ignore
                      const currentValue = props.values[filterDef.name];
                      const newValue = filtersGetInitialValue(filterDef, type);

                      if (
                        JSON.stringify(currentValue) !==
                        JSON.stringify(newValue)
                      ) {
                        props.form.change(
                          // @ts-ignore
                          filterDef.name,
                          newValue
                        );
                        deletedFieldsValueChanged = true;
                      }
                    }
                  });
                  props.form.resumeValidation();
                }

                setVisibleFields?.(newFields);
                onClose();

                // React final form nezvLádne rerender (přes setVisibleFields) současně s handle submit, proto submit pro promítnutí odmazaných polí dáme asynchronně
                if (deletedFields.length > 0 && deletedFieldsValueChanged) {
                  setTimeout(() => {
                    props.handleSubmit();
                  }, 1);
                }
              }}
            />
          ));
        };
        const clearFilters = () => {
          props.form.reset(
            getClearFilter({ type, filtersDef, initialFilter: false })
          );
        };

        const clearFiltersToDefault = () => {
          props.form.reset(
            getClearFilter({ type, filtersDef, initialFilter: true })
          );
          // @ts-ignore v nové verzi 4.2 uděláno správně, metoda by neměla mít povinný parametr
          setVisibleFields?.(initialVisibleFields);
        };

        const savedFilterWithoutLastFilter = filterHook.savedFilters?.filter(
          (x) => x.id !== LAST_FILTER_ID
        );
        const lastFilter = filterHook.savedFilters?.find(
          (x) => x.id === LAST_FILTER_ID
        );
        const sortedSavedFilters = [
          ...(lastFilter ? [lastFilter] : []),
          ...savedFilterWithoutLastFilter,
        ];

        return (
          <div className={classNames(className, Classes.FILTERS)}>
            {filtersDef
              .filter(
                (x) =>
                  !realManageVisibleFields ||
                  !visibleFields ||
                  visibleFields?.indexOf(x.name) >= 0
              )
              .map((filter, index) => {
                let component = filtersMapUpdated[filter.name].component;
                const res = component({
                  filterDef: filter,
                  onSearch: onSearch,
                });
                return <React.Fragment key={index}>{res}</React.Fragment>;
              })}
            <ButtonToolbar>
              <ButtonGroup>
                <Button
                  style={'primary'}
                  onClick={onSearch}
                  dataTest={'search'}
                >
                  Vyhledat
                </Button>
                {visibleFilterProfiles && settingsKey && (
                  <Button
                    style="primary"
                    dropdown
                    menu={
                      <Menu>
                        {sortedSavedFilters?.length > 0 && (
                          <>
                            <MenuDivider title={'Uložené filtry'} />
                            {sortedSavedFilters?.map((filterItem) => (
                              <MenuItem
                                text={filterItem.nazev}
                                onClick={() => onSavedFilter(filterItem)}
                              />
                            ))}
                            <MenuDivider />
                          </>
                        )}
                        <MenuItem
                          text={'Uložit filtr jako…'}
                          icon={<Icon icon={mcSave} />}
                          onClick={() => {
                            filterHook.saveFilters(
                              props?.values,
                              visibleFields
                            );
                          }}
                        />
                        <MenuItem
                          disabled={
                            !filterHook.savedFilters ||
                            filterHook.savedFilters.length === 0
                          }
                          intent={'danger'}
                          text={'Odstranit filtr'}
                          onClick={() => {
                            filterHook.removeSavedFilters(
                              filterHook.savedFilters
                            );
                          }}
                          icon={<Icon icon={mcDelete} />}
                        />
                      </Menu>
                    }
                  />
                )}
              </ButtonGroup>
              {realManageVisibleFields && (
                <Button
                  style={'default'}
                  title={'Vybrat položky filtru'}
                  icon={<Icon icon={mcFilterConfig} />}
                  onClick={showManageVisibleFields}
                  dataTest={'manage-filter-fields'}
                />
              )}
              {visibleClearField && (
                <ButtonGroup>
                  <Button
                    style={'default'}
                    title={'Resetovat filtr'}
                    icon={<Icon icon={mcErase} />}
                    onClick={clearFilters}
                    dataTest={'clear-filter'}
                  />
                  <Button
                    style={'default'}
                    dropdown
                    menu={
                      <Menu>
                        <MenuItem
                          text={'Výchozí filtr'}
                          onClick={clearFiltersToDefault}
                        />
                      </Menu>
                    }
                  />
                </ButtonGroup>
              )}
            </ButtonToolbar>
          </div>
        );
      }}
    </Form>
  );
};
